import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";

import { Alert, Badge, Button, Card, FormField, SelectField } from "../../../components/ui";
import { ApiError } from "../../../lib/api/client";
import {
  resetVoicePersona,
  saveVoicePersona,
  type VoicePersona,
} from "../api/voiceApi";

const customizationLabel: Record<string, string> = {
  customized: "已自訂",
  default: "使用預設",
};

const customizationVariant: Record<string, string> = {
  customized: "success",
  default: "secondary",
};

export function PersonaCard({ persona }: { persona: VoicePersona | undefined }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [roleObjective, setRoleObjective] = useState("");
  const [notes, setNotes] = useState("");
  const [speechStyle, setSpeechStyle] = useState("");
  const [voiceGender, setVoiceGender] = useState("male");
  const [notice, setNotice] = useState<string>();
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    if (!persona) return;
    setName(persona.name);
    setRoleObjective(persona.roleObjective);
    setNotes(persona.notes);
    setSpeechStyle(persona.speechStyle);
    setVoiceGender(persona.voiceGender);
  }, [persona]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setNotice(undefined);
    setError(undefined);
    try {
      const next = await saveVoicePersona({
        name,
        roleObjective,
        notes,
        speechStyle,
        voiceGender,
      });
      queryClient.setQueryData(["voice", "persona"], next);
      setNotice("Persona 設定已更新。");
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Persona 設定儲存失敗，請稍後再試。");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setResetting(true);
    setNotice(undefined);
    setError(undefined);
    try {
      const next = await resetVoicePersona();
      queryClient.setQueryData(["voice", "persona"], next);
      setNotice("Persona 已恢復為預設設定。");
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Persona 重設失敗，請稍後再試。");
    } finally {
      setResetting(false);
    }
  };

  return (
    <Card title="AI Persona 設定">
      <p className="text-muted mb-3">
        這裡設定語音助教的角色、回答風格與 Voice 性別。完整 system prompt 仍由後端依模板組裝，不會直接回傳到前端。
      </p>
      {notice && <Alert variant="success">{notice}</Alert>}
      {error && <Alert variant="danger">{error}</Alert>}
      <div className="mb-3">
        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
          <span className="text-muted">目前模式</span>{" "}
          <Badge
            variant={
              customizationVariant[persona?.isCustomized ? "customized" : "default"] ?? "secondary"
            }
          >
            {customizationLabel[persona?.isCustomized ? "customized" : "default"] ?? "使用預設"}
          </Badge>
        </div>
        <div className="small text-muted">Defaults 版本：{persona?.defaultsVersion ?? 1}</div>
      </div>
      <form onSubmit={(event) => void submit(event)}>
        <FormField label="AI 名稱" value={name} onChange={(event) => setName(event.target.value)} maxLength={60} />
        <label className="form-label font-weight-bold" htmlFor="voice-persona-role-objective">
          任務與角色
        </label>
        <textarea
          id="voice-persona-role-objective"
          className="form-control mb-3"
          rows={4}
          maxLength={2000}
          value={roleObjective}
          onChange={(event) => setRoleObjective(event.target.value)}
        />
        <label className="form-label font-weight-bold" htmlFor="voice-persona-notes">
          注意事項
        </label>
        <textarea
          id="voice-persona-notes"
          className="form-control mb-3"
          rows={7}
          maxLength={6000}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
        <label className="form-label font-weight-bold" htmlFor="voice-persona-speech-style">
          說話語調
        </label>
        <textarea
          id="voice-persona-speech-style"
          className="form-control mb-3"
          rows={4}
          maxLength={2000}
          value={speechStyle}
          onChange={(event) => setSpeechStyle(event.target.value)}
        />
        <SelectField
          label="Voice 性別"
          value={voiceGender}
          onChange={(event) => setVoiceGender(event.target.value)}
        >
          <option value="male">男性</option>
          <option value="female">女性</option>
        </SelectField>
        <div className="d-flex flex-wrap gap-2">
          <Button type="submit" loading={saving}>
            儲存 Persona 設定
          </Button>
          <Button type="button" variant="secondary" loading={resetting} onClick={() => void reset()}>
            恢復預設
          </Button>
        </div>
      </form>
    </Card>
  );
}
